import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import { UFOThirdPartySegment } from '@atlaskit/react-ufo/segment';
import type { IframeSegmentEvent } from '@atlaskit/react-ufo/segment';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { CustomUIRenderer, ModalCustomUIRenderer } from './CustomUIRenderer';
import { UIKitRenderer } from './UIKitRenderer';

import {
	makeFrame,
	makeModalThreeLOPromptForCustomUI,
	makeThreeLOPromptForCustomUI,
} from '../../components';
import { ForgeUIExtensionAnalyticsContext } from '../../analytics';
import {
	type ForgeUIRendererProps,
	type ConfluenceCustomUIProps,
	type CloudOrWorkspaceId,
} from './types';
import { isCustomUI } from '../../utils/isCustomUI';
import {
	PerformanceAnalyticsProviderRollout,
	PerformanceAnalyticsContext,
} from '../../performance-profiling/PerformanceAnalyticsProvider';
import { PerformanceHoldAttributionAnchorRollout } from '../../performance-profiling/PerformanceHoldAttributionAnchor';
import { UserAccessProvider } from '../../context/user-access';

type Props = ForgeUIRendererProps & CloudOrWorkspaceId & ConfluenceCustomUIProps;

/**
 * Inner wrapper rendered inside PerformanceAnalyticsProviderRollout so it can
 * access PerformanceAnalyticsContext and wire up UFOThirdPartySegment's iframe
 * behavior via onRegisterIframeEventListener.
 */
function UFOThirdPartySegmentWithPerfTimeline({
	children,
	name,
}: {
	children: React.ReactNode;
	name: string;
}): React.JSX.Element {
	const timelineRef = useContext(PerformanceAnalyticsContext);

	const onRegisterIframeEventListener = useCallback(
		(listener: (event: IframeSegmentEvent) => void) => {
			const timeline = timelineRef?.current;
			if (!timeline) {
				return () => {};
			}
			timeline.listeners.add(listener);
			return () => {
				timeline.listeners.delete(listener);
			};
		},
		[timelineRef],
	);

	return (
		<UFOThirdPartySegment name={name} onRegisterIframeEventListener={onRegisterIframeEventListener}>
			{children}
		</UFOThirdPartySegment>
	);
}

export const ForgeUIRenderer = (props: Props): React.JSX.Element => {
	const {
		accountId,
		bridge,
		client,
		components,
		consentMessage,
		contextIds,
		customBridgeMethods,
		dialogs,
		entryPoint,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		hasModalConsentPrompt,
		height,
		width,
		isIframeHidden,
		isIframeResizable,
		getContextToken,
		loadingComponent,
		locale,
		localId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onConsentInitialization,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		onInitialRender,
		product,
		timezone,
		getModalWidth,
		onModalExtensionClose,
		onSuccess,
		onAppError,
		viewportDimensions,
		isEmbeddedMacro,
		embeddedParentLocalId,
		isEmbeddedUIKitInCustomUI,
	} = props;

	let cloudId: string | undefined;
	let workspaceId: string | undefined;

	if ('cloudId' in props) {
		cloudId = props.cloudId;
	} else if ('workspaceId' in props) {
		workspaceId = props.workspaceId;
	}

	const cloudOrWorkspaceId = cloudId ? { cloudId } : ({ workspaceId } as { workspaceId: string });

	const { id: extensionId, environmentId, environmentType } = extension;

	useEffect(() => {
		onInitialRender?.();
	}, [onInitialRender]);

	const coreData = useMemo(
		() => ({
			workspaceId,
			cloudId,
			localId: localId || extensionId,
			environmentId,
			environmentType,
		}),
		[cloudId, environmentId, environmentType, extensionId, localId, workspaceId],
	);

	const getThreeLOPrompt = useCallback(
		() => ({
			ThreeLOPrompt:
				modalExtension || hasModalConsentPrompt
					? makeModalThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							isBlanketHidden: true,
							onClose: onConsentModalClose,
						})
					: makeThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							onSuccess: onConsentSuccess,
							message: consentMessage,
							onInitialization: onConsentInitialization,
						}),
		}),
		[
			extension.properties.title,
			consentMessage,
			hasModalConsentPrompt,
			modalExtension,
			onConsentSuccess,
			onConsentModalClose,
			onConsentInitialization,
		],
	);

	const customUIProps = {
		accountId,
		bridge,
		client,
		consentMessage,
		contextIds,
		coreData,
		customBridgeMethods,
		dialogs,
		entryPoint,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		getContextToken,
		getThreeLOPrompt,
		height,
		loadingComponent,
		locale,
		localId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		product,
		timezone,
		isIframeHidden,
		isIframeResizable,
		isEmbeddedMacro,
		embeddedParentLocalId,
	};

	const uiKitEmbeddedMacroProps = {
		isEmbeddedMacro,
		embeddedParentLocalId,
	};

	const [viewOnClose, setViewOnClose] = useState<() => Promise<void> | void>();

	// this function has known deps listed already in deps array
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const FrameResizable = useCallback(
		makeFrame((inputProps) => {
			const { height, width } = inputProps;

			// If height or width is provided by app code, the iframe does not automatically resize to contents
			const isIframeResizable = height || width ? false : true;

			return (
				<CustomUIRenderer
					{...customUIProps}
					extension={{
						...extension,
						properties: {
							...extension.properties,
							resource: inputProps.resource,
						},
					}}
					// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
					entryPoint={undefined}
					// Ensures Frame is always resized to the full height of the content
					// Required because Jira leaves isIframeResizable as false
					isIframeResizable={isIframeResizable}
					height={height}
					width={width}
				/>
			);
		}),
		// need eslint here as re-render only depends on extensionData & extensionPayload
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[customUIProps.extensionData, customUIProps.extensionPayload, extension],
	);

	if (fg('platform_ufo_3p_segment_timings')) {
		return (
			<AtlassianRelayEnvironmentProvider>
				<ForgeUIExtensionAnalyticsContext
					extensionId={props.extension.id}
					localId={props.localId || props.extension.id}
				>
					<PerformanceAnalyticsProviderRollout
						profileKey={`${props.extension.id}-${props.extension.key}-${props.entryPoint}`}
						appType={isCustomUI(props.extension, props.entryPoint) ? 'CustomUI' : 'UIKit'}
						forgeEnvironment={props.extension.environmentType}
						extensionType={props.extension.type}
						appVersion={props.extension.appVersion}
					>
						<UFOThirdPartySegmentWithPerfTimeline name="forge-ui-app-content-root">
							<PerformanceHoldAttributionAnchorRollout>
								<UserAccessProvider accountId={accountId}>
									{/* hidden span used for testing purposes and to identify that this renderer is being used when toggling the feature flag in products*/}
									<span hidden id="forge-ui-renderer" />
									{!isCustomUI(extension, entryPoint) ? (
										<UIKitRenderer
											coreData={coreData}
											getThreeLOPrompt={getThreeLOPrompt}
											accountId={accountId}
											client={client}
											contextIds={contextIds}
											extension={extension}
											extensionData={extensionData}
											extensionViewData={extensionViewData}
											product={product}
											bridge={{
												// spread down here so product can override the default onClose
												...(modalExtension && fg('forge-ui-on-close-bridge-method')
													? {
															onClose: (callback) => {
																if (extension.environmentType === 'PRODUCTION') {
																	// eslint-disable-next-line no-console
																	console.warn(
																		'Forge view.onClose (EAP) is not available for production apps. The callback will not be registered.',
																	);
																	return false;
																}
																setViewOnClose(() => callback.data);
																return true;
															},
														}
													: {}),
												...bridge,
											}}
											customBridgeMethods={customBridgeMethods}
											components={{
												Frame: fg('ditto-fcf-frame-support-on-issue-create')
													? FrameResizable
													: makeFrame((inputProps) => {
															return (
																<CustomUIRenderer
																	{...customUIProps}
																	extension={{
																		...extension,
																		properties: { ...extension.properties, ...inputProps },
																	}}
																	// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
																	entryPoint={undefined}
																	// Ensures Frame is always resized to the full height of the content
																	// Required because Jira leaves isIframeResizable as false
																	isIframeResizable={true}
																/>
															);
														}),
												...components,
											}}
											consentMessage={consentMessage}
											entryPoint={entryPoint}
											getContextToken={getContextToken}
											locale={locale}
											localId={localId}
											mentionProvider={mentionProvider}
											modalExtension={modalExtension}
											onConsentSuccess={onConsentSuccess}
											onForgeDocUpdated={onForgeDocUpdated}
											timezone={timezone}
											onConsentModalClose={onConsentModalClose}
											onError={onError}
											loadingComponent={loadingComponent}
											extensionPayload={extensionPayload}
											onSuccess={onSuccess}
											{...cloudOrWorkspaceId}
											onAppError={onAppError}
											viewportDimensions={viewportDimensions}
											viewOnClose={viewOnClose}
											isEmbeddedUIKitInCustomUI={isEmbeddedUIKitInCustomUI}
											{...uiKitEmbeddedMacroProps}
										/>
									) : modalExtension ? (
										// getModalWidth not applied here since all modal extensions are defaulted to medium width
										<ModalCustomUIRenderer
											{...customUIProps}
											bridge={{
												...(modalExtension && fg('forge-ui-on-close-bridge-method')
													? {
															onClose: (callback) => {
																if (extension.environmentType === 'PRODUCTION') {
																	// eslint-disable-next-line no-console
																	console.warn(
																		'Forge view.onClose is not available for production apps during EAP. The callback will not be registered.',
																	);
																	return false;
																}
																setViewOnClose(() => callback.data);
																// stores the callback for when modal is closed
																return true;
															},
														}
													: {}),
												// spread down here so product can override the default onClose
												...customUIProps.bridge,
											}}
											modalOnClose={onModalExtensionClose}
											closeOnEscape={modalExtension?.shouldCloseOnEscapePress ?? true}
											closeOnOverlayClick={modalExtension?.shouldCloseOnOverlayClick ?? true}
											viewOnClose={viewOnClose}
										/>
									) : (
										<CustomUIRenderer
											{...customUIProps}
											getModalWidth={getModalWidth}
											width={width}
										/>
									)}
								</UserAccessProvider>
							</PerformanceHoldAttributionAnchorRollout>
						</UFOThirdPartySegmentWithPerfTimeline>
					</PerformanceAnalyticsProviderRollout>
				</ForgeUIExtensionAnalyticsContext>
			</AtlassianRelayEnvironmentProvider>
		);
	}

	// TODO: Delete after cleaning up 'platform_ufo_3p_segment_timings' flag
	return (
		<AtlassianRelayEnvironmentProvider>
			<ForgeUIExtensionAnalyticsContext
				extensionId={props.extension.id}
				localId={props.localId || props.extension.id}
			>
				<UFOThirdPartySegment name="forge-ui-app-content-root">
					<PerformanceAnalyticsProviderRollout
						profileKey={`${props.extension.id}-${props.extension.key}-${props.entryPoint}`}
						appType={isCustomUI(props.extension, props.entryPoint) ? 'CustomUI' : 'UIKit'}
						forgeEnvironment={props.extension.environmentType}
						extensionType={props.extension.type}
						appVersion={props.extension.appVersion}
					>
						<PerformanceHoldAttributionAnchorRollout>
							<UserAccessProvider accountId={accountId}>
								{/* hidden span used for testing purposes and to identify that this renderer is being used when toggling the feature flag in products*/}
								<span hidden id="forge-ui-renderer" />
								{!isCustomUI(extension, entryPoint) ? (
									<UIKitRenderer
										coreData={coreData}
										getThreeLOPrompt={getThreeLOPrompt}
										accountId={accountId}
										client={client}
										contextIds={contextIds}
										extension={extension}
										extensionData={extensionData}
										extensionViewData={extensionViewData}
										product={product}
										bridge={{
											// spread down here so product can override the default onClose
											...(modalExtension && fg('forge-ui-on-close-bridge-method')
												? {
														onClose: (callback) => {
															if (extension.environmentType === 'PRODUCTION') {
																// eslint-disable-next-line no-console
																console.warn(
																	'Forge view.onClose (EAP) is not available for production apps. The callback will not be registered.',
																);
																return false;
															}
															setViewOnClose(() => callback.data);
															return true;
														},
													}
												: {}),
											...bridge,
										}}
										customBridgeMethods={customBridgeMethods}
										components={{
											Frame: fg('ditto-fcf-frame-support-on-issue-create')
												? FrameResizable
												: makeFrame((inputProps) => {
														return (
															<CustomUIRenderer
																{...customUIProps}
																extension={{
																	...extension,
																	properties: { ...extension.properties, ...inputProps },
																}}
																// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
																entryPoint={undefined}
																// Ensures Frame is always resized to the full height of the content
																// Required because Jira leaves isIframeResizable as false
																isIframeResizable={true}
															/>
														);
													}),
											...components,
										}}
										consentMessage={consentMessage}
										entryPoint={entryPoint}
										getContextToken={getContextToken}
										locale={locale}
										localId={localId}
										mentionProvider={mentionProvider}
										modalExtension={modalExtension}
										onConsentSuccess={onConsentSuccess}
										onForgeDocUpdated={onForgeDocUpdated}
										timezone={timezone}
										onConsentModalClose={onConsentModalClose}
										onError={onError}
										loadingComponent={loadingComponent}
										extensionPayload={extensionPayload}
										onSuccess={onSuccess}
										{...cloudOrWorkspaceId}
										onAppError={onAppError}
										viewportDimensions={viewportDimensions}
										viewOnClose={viewOnClose}
										{...uiKitEmbeddedMacroProps}
										isEmbeddedUIKitInCustomUI={isEmbeddedUIKitInCustomUI}
									/>
								) : modalExtension ? (
									// getModalWidth not applied here since all modal extensions are defaulted to medium width
									<ModalCustomUIRenderer
										{...customUIProps}
										bridge={{
											...(modalExtension && fg('forge-ui-on-close-bridge-method')
												? {
														onClose: (callback) => {
															if (extension.environmentType === 'PRODUCTION') {
																// eslint-disable-next-line no-console
																console.warn(
																	'Forge view.onClose is not available for production apps during EAP. The callback will not be registered.',
																);
																return false;
															}
															setViewOnClose(() => callback.data);
															// stores the callback for when modal is closed
															return true;
														},
													}
												: {}),
											// spread down here so product can override the default onClose
											...customUIProps.bridge,
										}}
										modalOnClose={onModalExtensionClose}
										closeOnEscape={modalExtension?.shouldCloseOnEscapePress ?? true}
										closeOnOverlayClick={modalExtension?.shouldCloseOnOverlayClick ?? true}
										viewOnClose={viewOnClose}
									/>
								) : (
									<CustomUIRenderer
										{...customUIProps}
										getModalWidth={getModalWidth}
										width={width}
									/>
								)}
							</UserAccessProvider>
						</PerformanceHoldAttributionAnchorRollout>
					</PerformanceAnalyticsProviderRollout>
				</UFOThirdPartySegment>
			</ForgeUIExtensionAnalyticsContext>
		</AtlassianRelayEnvironmentProvider>
	);
};
